/**
 * Game state store — Zustand wrapper around `@sai-clicker/game-core` reducers.
 *
 * This is a thin shell. All game math runs through `applyTap` /
 * `performCrystallization` from game-core; the store only:
 *   1. Holds the current state and a few UI-side flags (fired activations,
 *      live floating phrases on screen).
 *   2. Triggers reducers in response to user actions.
 *   3. Subscribes the UI via React hooks.
 *
 * **Persistence** (2026-05-25, JB carte-blanche): локально через
 * localStorage key `sai-game-state-v1`. Перезагрузка страницы / закрытие
 * вкладки больше не теряют прогресс — игрок возвращается ровно туда где
 * остановился. Это критично для return-greeting, funnel firsts и
 * вообще любой retention механики.
 *
 * Что persistится:
 *   - `state` (полный GameState — resources, stats, atlas, lessons,
 *     upgrades, profile, epoch, globalBoosts, cooldownMs)
 *   - `firedActivationIds` (чтобы активации Сай не повторялись)
 *   - `hasSeenCrystallize` (sticky button visibility flag)
 *
 * Что НЕ persistится (runtime UI state):
 *   - `liveFloatingPhrases` — animations queue
 *   - `activeActivation` — overlay state
 *   - `pendingUpgradeShowcase` — overlay state
 *   - `recentlyUnlockedLessons` — toast banner queue
 *   - `multiplier` — computed из state на rehydrate
 *
 * Map/Set serialization: Zustand persist JSON-serializes — нативные Map
 * и Set теряют структуру. Custom partialize/merge конвертирует:
 *   Map<K,V> → [[K,V], ...]  (Array.from(map.entries()))
 *   Set<T>   → [T, ...]      (Array.from(set))
 *
 * При смене shape GameState в будущем бампать `version` и добавить
 * `migrate()` в persist config (zustand поддерживает schema migrations).
 *
 * Server-side authoritative state — будущая работа когда появится
 * backend; пока локального persistence хватает для альфы.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  ACTIVATIONS_ALPHA,
  ALL_PHRASES,
  LESSONS_ALPHA,
  UPGRADES_ALPHA,
} from '@sai-clicker/content';
import {
  applyTap,
  attemptPurchase,
  composeMultiplierFromState,
  createInitialState,
  getTotalUpgrades,
  performCrystallization,
  previewCrystallization,
  type GameState,
  type LessonDefinition,
  type MultiplierBreakdown,
  type PhraseEntry,
  type PurchaseResult,
  type SaiActivation,
} from '@sai-clicker/game-core';
import { useOnboardingStore } from '../onboarding/store.js';
import { playSound } from '../sounds/player.js';
import { emit } from '../telemetry/emit.js';
import { markFunnel } from '../telemetry/funnel.js';
import { VISUAL_ARTIFACTS_CATALOG } from '../visual-artifacts/catalog.js';
import { evaluateArtifacts } from '../visual-artifacts/evaluator.js';
import { useArtifactsStore } from '../visual-artifacts/store.js';
import { extractEvalState } from '../visual-artifacts/types.js';
import {
  deserializeGameState,
  serializeGameState,
  type SerializedGameState,
} from './gameStateSerializer.js';

/** A floating phrase currently animating above the Spark. */
export interface LiveFloatingPhrase {
  readonly key: number; // monotonically increasing render key
  readonly phrase: PhraseEntry;
  readonly awarenessesGained: number;
  readonly originX: number; // 0..1, fraction of viewport
  readonly originY: number;
}

/**
 * Pending purchase showcase — `UpgradeShowcaseOverlay` reads the head
 * and shows a brief reveal moment. Cleared by overlay dismiss.
 * Per-purchase (not per-first-purchase) so every level feels celebrated.
 */
export interface PendingUpgradeShowcase {
  readonly upgradeId: string;
  readonly newLevel: number;
  readonly costPaid: number;
  readonly currency: 'awarenesses' | 'spirit_balls';
  /** Wall-clock when fired — used as React key so overlay restarts cleanly. */
  readonly firedAt: number;
}

interface GameStore {
  // Authoritative game state
  readonly state: GameState;

  // UI-side bookkeeping
  readonly liveFloatingPhrases: readonly LiveFloatingPhrase[];
  readonly activeActivation: SaiActivation | null;
  readonly firedActivationIds: ReadonlySet<string>;
  readonly recentlyUnlockedLessons: readonly LessonDefinition[];
  readonly multiplier: MultiplierBreakdown;
  /** Most recent upgrade purchase awaiting overlay reveal. */
  readonly pendingUpgradeShowcase: PendingUpgradeShowcase | null;
  /**
   * Sticky flag — true the moment the player FIRST has enough awareness
   * to crystallize (or after they've done it at least once). Drives
   * CrystallizeButton's visibility: it appears once and stays forever
   * after, toggling enabled/disabled instead of vanishing. JB-requested
   * 2026-05-24 to stop the button "disappearing" feel.
   */
  readonly hasSeenCrystallize: boolean;

  // Actions
  /**
   * Apply a tap at the given normalized origin.
   * Returns `true` if the tap was accepted (cooldown elapsed), `false`
   * if it was silently rejected. Callers (Spark.tsx) use this to gate
   * the ding + ripple — no animation/sound on a rejected tap.
   */
  tap(originX: number, originY: number): boolean;
  crystallize(): void;
  dismissActivation(): void;
  removeFloatingPhrase(key: number): void;
  acknowledgeLessons(): void;
  setDisplayName(name: string | null): void;
  /**
   * Attempt to buy the next level of an upgrade by id. Returns the result
   * shape from game-core so the UI can show a denial reason (locked /
   * insufficient_funds) or play the success sound + show artifact reveal.
   */
  purchaseUpgrade(upgradeId: string): PurchaseResult;
  /**
   * Bulk-buy: купить максимально возможное количество уровней апгрейда
   * за один клик. JB 2026-05-26: late-game становится click-spam'ом, max
   * button решает проблему.
   *
   * Внутри — loop attemptPurchase'а пока есть ресурсы и не достигнут
   * maxLevel. Sound + showcase + artifact check ОДИН РАЗ в конце с
   * cumulative cost / final level. Один analytics event с totalCost.
   *
   * Returns: количество купленных уровней (0 если не было денег даже
   * на первый).
   */
  purchaseUpgradeMax(upgradeId: string): number;
  /** Called by UpgradeShowcaseOverlay to clear the pending entry. */
  dismissUpgradeShowcase(): void;
  /**
   * Reset the current rhythm streak to 0 (без изменения `tapsInRhythmBest`).
   * JB 2026-05-25: «стрик должен фэйдануться постепенно если не продолжать
   * в ритме». Идемпотентно — повторные вызовы безопасны.
   * Вызывается через setTimeout из tap() — если игрок не тапнул в течение
   * STREAK_FADE_AFTER_MS, streak обнуляется и StreakIndicator fade-out.
   */
  fadeStreak(): void;
  /**
   * Принять награду от атласа (за первый просмотр entry).
   * JB 2026-05-26: «в атласе должны быть какие то награды за первый клик
   * на каждое новое открытое что либо».
   * Идемпотентно — atlas store сам контролирует mark-viewed.
   */
  grantAtlasReward(awarenesses: number, spirit_balls: number): void;
}

let _nextFloatingKey = 1;

/**
 * 2026-05-26 perf cap: max concurrent floating phrases на экране.
 * На быстром ритме durationSec фраз (1.6–5.5s) × 5 тапов/сек могло
 * приводить к 25+ одновременным motion.span — серьёзная нагрузка
 * compositor'а на mobile. 12 — sweet spot: достаточно visual variety,
 * не перегружает.
 */
const MAX_FLOATING_PHRASES = 12;

/**
 * Через сколько ms тишины (без новых тапов) streak считается «потерянным»
 * и StreakIndicator фейдается. JB 2026-05-25: «стрик должен через какое
 * то колво секунд фэйдануться постепенно».
 *
 * 2500ms выбрано так чтобы:
 *   - При cooldown 1500ms игрок имеет ещё ~1 сек grace period после
 *     обычного интервала тапов
 *   - Если игрок остановился и думает — за 2.5 сек видит как стрик уходит
 *   - Если просто медленно тапает — стрик не теряется случайно
 */
const STREAK_FADE_AFTER_MS = 2_500;
let _streakFadeTimer: number | null = null;

function composeMultiplierForState(state: GameState): MultiplierBreakdown {
  return composeMultiplierFromState({
    state,
    lessonsCatalog: LESSONS_ALPHA,
    allPhrases: ALL_PHRASES,
    upgradesCatalog: UPGRADES_ALPHA,
  });
}

/**
 * Check whether any milestone artifacts just unlocked after a state change.
 * Pulls from the freshest gameState + freshest artifacts store. Pushes
 * any newly-triggered artifacts to the artifacts store's reveal queue.
 *
 * Called from `tap()` (after accepted tap) and `crystallize()` (after
 * conversion). Cheap — iterates ~7 catalog entries.
 *
 * `activationsSeen` is approximated from `firedActivationIds.size` — close
 * enough until we add a real seen-count to game state.
 */
function checkArtifactsAfter(opts: {
  readonly state: GameState;
  readonly firedActivationIds: ReadonlySet<string>;
}): void {
  const artStore = useArtifactsStore.getState();
  const evalState = extractEvalState({
    state: opts.state,
    activationsSeen: opts.firedActivationIds.size,
    hasProfileName: opts.state.profile.displayName !== null,
    now: new Date(),
    upgradeLevels: opts.state.purchasedUpgrades,
    totalUpgrades: getTotalUpgrades(opts.state.purchasedUpgrades),
    phrasesCollected: opts.state.atlasCollected.size,
  });
  const newly = evaluateArtifacts({
    state: evalState,
    alreadyUnlocked: artStore.unlocked,
    catalog: VISUAL_ARTIFACTS_CATALOG,
  });
  if (newly.length > 0) {
    artStore.recordNewlyUnlocked(newly);
  }
}

// ---------------------------------------------------------------------------
// Persistence — Map/Set serializers
// ---------------------------------------------------------------------------

// SerializedGameState shape + serializer/deserializer — single source of
// truth в ./gameStateSerializer.ts. Используется и здесь (localStorage
// persist) и в telemetry/cloudSync.ts (over-the-wire cloud sync).

interface PersistedGameStore {
  readonly state: SerializedGameState;
  readonly firedActivationIds: readonly string[];
  readonly hasSeenCrystallize: boolean;
}

/** localStorage key — version в имени для будущих миграций. */
const PERSIST_KEY = 'sai-game-state-v1';

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => {
      const initialState = createInitialState();
      return {
        state: initialState,
        liveFloatingPhrases: [],
        activeActivation: null,
        firedActivationIds: new Set(),
        recentlyUnlockedLessons: [],
        multiplier: composeMultiplierForState(initialState),
        pendingUpgradeShowcase: null,
        hasSeenCrystallize: false,

        tap(originX, originY) {
          const current = get();
          const result = applyTap({
            state: current.state,
            lessonsCatalog: LESSONS_ALPHA,
            allPhrases: ALL_PHRASES,
            activationsCatalog: ACTIVATIONS_ALPHA,
            upgradesCatalog: UPGRADES_ALPHA,
            firedActivationIds: current.firedActivationIds,
            now: Date.now(),
          });

          // Cooldown rejection — silently update only the rejected-counter.
          // No phrase, no awareness, no sound (Spark.tsx checks return value
          // to know whether to play the ding).
          if (!result.accepted) {
            set({ state: result.state });
            return false;
          }

          // From here on, the tap was accepted — result.phrase is non-null.
          const phrase = result.phrase!;

          // Numeric phrases show the REAL awareness gain, not the static text
          // from the pool. The pool's weights still drive how often "a number"
          // (vs a word, vs a milestone) is shown — but the number itself is
          // always the true gain. Otherwise the player sees "+5" while the
          // counter ticks up by 1 — broken trust.
          const displayPhrase =
            phrase.layer === 'numeric'
              ? { ...phrase, text: `+${result.awarenessesGained.toString()}` }
              : phrase;

          const liveEntry: LiveFloatingPhrase = {
            key: _nextFloatingKey++,
            phrase: displayPhrase,
            awarenessesGained: result.awarenessesGained,
            originX,
            originY,
          };

          // Deduplicate special phrases — a milestone phrase replaces any
          // previously-active milestone so two never overlap on screen.
          const isNewSpecial = phrase.special !== undefined;
          const dedupedLive = isNewSpecial
            ? current.liveFloatingPhrases.filter((p) => p.phrase.special === undefined)
            : current.liveFloatingPhrases;
          // 2026-05-26 perf: cap concurrent floating phrases. На быстром
          // ритме (3-5 тапов/сек) с durationSec 1.6-5.5s могло накапливаться
          // до 25+ active motion.span — заметный compositor jank в TG bot.
          // Cap 12 = достаточно для visual variety, не перегружает GPU.
          // Drop старейшие (FIFO) когда превышаем.
          const trimmedLive =
            dedupedLive.length >= MAX_FLOATING_PHRASES
              ? dedupedLive.slice(dedupedLive.length - MAX_FLOATING_PHRASES + 1)
              : dedupedLive;

          const nextFired = result.triggeredActivation
            ? new Set([...current.firedActivationIds, result.triggeredActivation.id])
            : current.firedActivationIds;

          // Sticky-button flag: once the player has enough awareness to
          // crystallize, the button stays visible forever after, even after
          // they spend the pool back to zero. Only set; never unset.
          const crystallizePreview = previewCrystallization(
            result.state.resources.awarenesses,
            result.state.epoch,
          );
          const hasSeenCrystallize =
            current.hasSeenCrystallize || crystallizePreview.spiritBallsToGain > 0;

          set({
            state: result.state,
            liveFloatingPhrases: [...trimmedLive, liveEntry],
            activeActivation: result.triggeredActivation ?? current.activeActivation,
            firedActivationIds: nextFired,
            recentlyUnlockedLessons:
              result.newlyUnlockedLessons.length > 0
                ? [...current.recentlyUnlockedLessons, ...result.newlyUnlockedLessons]
                : current.recentlyUnlockedLessons,
            multiplier: composeMultiplierForState(result.state),
            hasSeenCrystallize,
          });
          // After every accepted tap, check if any milestone artifacts just
          // crossed their trigger. Cheap (~7 catalog entries iterated).
          checkArtifactsAfter({ state: result.state, firedActivationIds: nextFired });

          // ---- Funnel firsts (UX flow analytics) ----
          // markFunnel sам идемпотентно — повторные no-op. См. telemetry/funnel.ts.
          markFunnel('first_tap');
          markFunnel('first_phrase_shown');
          if (!current.hasSeenCrystallize && hasSeenCrystallize) {
            // Sticky-flag только что переключился → игрок впервые в состоянии
            // «могу кристаллизовать» (хотя ещё не нажал).
            markFunnel('first_crystallize_ready');
          }
          if (result.triggeredActivation !== null) {
            markFunnel('first_activation_seen');
          }
          if (result.newlyUnlockedLessons.length > 0) {
            markFunnel('first_lesson_unlocked');
          }
          // Streak entered — игрок впервые в текущей сессии стрика (newRhythm 1→2).
          // tapsInRhythmCurrent в состоянии ПОСЛЕ tap'а.
          const prevStreak = current.state.stats.tapsInRhythmCurrent;
          const nextStreak = result.state.stats.tapsInRhythmCurrent;
          if (prevStreak === 0 && nextStreak === 1) {
            // не интересный single tap — игнорируем (streak ещё не начался)
          } else if (prevStreak < 2 && nextStreak >= 2) {
            emit('tap.streak.entered', { length: nextStreak, epoch: result.state.epoch });
          }

          // Re-arm the streak-fade timer. If the player goes silent for
          // STREAK_FADE_AFTER_MS, fadeStreak() resets tapsInRhythmCurrent
          // → 0 → StreakIndicator AnimatePresence fades out smoothly.
          if (_streakFadeTimer !== null) {
            window.clearTimeout(_streakFadeTimer);
          }
          _streakFadeTimer = window.setTimeout(() => {
            get().fadeStreak();
          }, STREAK_FADE_AFTER_MS);

          return true;
        },

        crystallize() {
          const current = get();
          const preview = previewCrystallization(
            current.state.resources.awarenesses,
            current.state.epoch,
          );
          if (preview.spiritBallsToGain === 0) {
            emit('crystallization.skipped_zero', {
              current_awarenesses: current.state.resources.awarenesses,
            });
            return;
          }
          // 2026-05-26 BUG FIX: streak fade timer мог сработать ПОСЛЕ
          // crystallize, обнуляя tapsInRhythmCurrent в новом state.
          // Race condition: tap → таймер 2.5s армируется → crystallize
          // → стрик в новом state ≠ 0 (если был активный) → таймер
          // срабатывает и обнуляет. Очищаем при crystallize.
          if (_streakFadeTimer !== null) {
            window.clearTimeout(_streakFadeTimer);
            _streakFadeTimer = null;
          }
          const prevEpoch = current.state.epoch;
          const result = performCrystallization({
            state: current.state,
            lessonsCatalog: LESSONS_ALPHA,
            allPhrases: ALL_PHRASES,
            activationsCatalog: ACTIVATIONS_ALPHA,
            upgradesCatalog: UPGRADES_ALPHA,
            firedActivationIds: current.firedActivationIds,
            now: Date.now(),
          });

          const nextFired = result.triggeredActivation
            ? new Set([...current.firedActivationIds, result.triggeredActivation.id])
            : current.firedActivationIds;

          // Audio: the glockenspiel chord on every crystallization, plus the
          // bigger epoch-transition swell if this crystallization advanced
          // the player to a new epoch.
          playSound('crystal_chord');
          emit('crystallization.completed', {
            epoch: result.state.epoch,
            // Actual gained — already includes crystallizationBonus from upgrades.
            spirit_balls_gained: result.spiritBallsGained,
          });
          if (result.state.epoch > prevEpoch) {
            // Stagger so the chord lands first, then the swell opens.
            setTimeout(() => {
              playSound('epoch_transition');
            }, 800);
            emit('epoch.advanced', {
              from_epoch: prevEpoch,
              to_epoch: result.state.epoch,
              time_in_epoch_min: 0, // TODO: track epoch entry time in state
            });
          }

          set({
            state: result.state,
            activeActivation: result.triggeredActivation ?? current.activeActivation,
            firedActivationIds: nextFired,
            recentlyUnlockedLessons:
              result.newlyUnlockedLessons.length > 0
                ? [...current.recentlyUnlockedLessons, ...result.newlyUnlockedLessons]
                : current.recentlyUnlockedLessons,
            multiplier: composeMultiplierForState(result.state),
          });
          // Crystallization is a peak moment for artifact unlocks (1st/10th/50th
          // crystallization triggers, spirit_balls thresholds, epoch_reached).
          checkArtifactsAfter({ state: result.state, firedActivationIds: nextFired });

          // Funnel firsts — крупный момент.
          markFunnel('first_crystallize');
          if (result.state.epoch > prevEpoch) {
            markFunnel('first_epoch_advance');
          }
        },

        dismissActivation() {
          set({ activeActivation: null });
        },

        removeFloatingPhrase(key) {
          set((s) => ({
            liveFloatingPhrases: s.liveFloatingPhrases.filter((p) => p.key !== key),
          }));
        },

        acknowledgeLessons() {
          set({ recentlyUnlockedLessons: [] });
        },

        setDisplayName(name) {
          set((s) => ({
            state: {
              ...s.state,
              profile: { ...s.state.profile, displayName: name },
            },
          }));
          // Name-set unlocks `name_signature` artifact via profileNameSet trigger.
          const fresh = get();
          checkArtifactsAfter({ state: fresh.state, firedActivationIds: fresh.firedActivationIds });
        },

        purchaseUpgrade(upgradeId) {
          const current = get();
          const upgrade = UPGRADES_ALPHA.find((u) => u.id === upgradeId);
          if (upgrade === undefined) {
            return { ok: false, reason: 'locked' };
          }
          const result = attemptPurchase({
            upgrade,
            currentEpoch: current.state.epoch,
            purchased: current.state.purchasedUpgrades,
            resources: current.state.resources,
          });
          if (!result.ok) {
            emit('upgrade.purchase_denied', {
              upgrade_id: upgradeId,
              reason: result.reason,
            });
            return result;
          }
          const nextState: GameState = {
            ...current.state,
            resources: result.resources,
            purchasedUpgrades: result.purchased,
          };
          set({
            state: nextState,
            multiplier: composeMultiplierForState(nextState),
            pendingUpgradeShowcase: {
              upgradeId,
              newLevel: result.newLevel,
              costPaid: result.costPaid,
              currency: upgrade.currency,
              firedAt: Date.now(),
            },
          });
          // Audio: small or big based on currency. Both will be silent until
          // JB drops the Suno files per docs/SOUND_GENERATION_TASKS.md.
          const soundId =
            upgrade.currency === 'spirit_balls'
              ? 'big_upgrade_purchased'
              : 'small_upgrade_purchased';
          playSound(soundId);
          // Visible-change moment — even when this exact level doesn't unlock
          // a milestone artifact, we play the reveal swell so the player FEELS
          // the purchase. (Doc 22 visible_change_reveal is tier 4 = peak.)
          playSound('visible_change_reveal');
          emit('upgrade.purchased', {
            upgrade_id: upgradeId,
            new_level: result.newLevel,
            cost_paid: result.costPaid,
            currency: upgrade.currency,
          });
          // Purchase may unlock an artifact (`upgradePurchasedExactly` trigger).
          checkArtifactsAfter({
            state: nextState,
            firedActivationIds: current.firedActivationIds,
          });
          // Funnel: первая покупка апгрейда — критический funnel-шаг.
          markFunnel('first_upgrade_purchased');
          return result;
        },

        purchaseUpgradeMax(upgradeId) {
          // Loop attemptPurchase пока есть ресурсы и не reached maxLevel.
          // Накапливаем totalCost + finalLevel, обновляем state в конце
          // через ОДИН set — это даёт single re-render, single showcase,
          // single sound, single analytics emit.
          const upgrade = UPGRADES_ALPHA.find((u) => u.id === upgradeId);
          if (upgrade === undefined) return 0;
          const cur = get();
          let workingState = cur.state;
          let totalCost = 0;
          let purchasedCount = 0;
          let lastResultLevel = 0;
          // Защита от runaway loop'а — апгрейды имеют maxLevel ≤ 50 в
          // alpha, но кап на 100 хедж от багов в каталоге.
          const HARD_CAP = 100;
          for (let i = 0; i < HARD_CAP; i++) {
            const result = attemptPurchase({
              upgrade,
              currentEpoch: workingState.epoch,
              purchased: workingState.purchasedUpgrades,
              resources: workingState.resources,
            });
            if (!result.ok) break;
            purchasedCount++;
            totalCost += result.costPaid;
            lastResultLevel = result.newLevel;
            workingState = {
              ...workingState,
              resources: result.resources,
              purchasedUpgrades: result.purchased,
            };
          }
          if (purchasedCount === 0) {
            // Никаких покупок — analytics emit denied чтобы видеть «игрок
            // тыкнул max но не смог купить даже один уровень».
            emit('upgrade.purchase_denied', {
              upgrade_id: upgradeId,
              reason: 'insufficient_funds',
            });
            return 0;
          }
          set({
            state: workingState,
            multiplier: composeMultiplierForState(workingState),
            pendingUpgradeShowcase: {
              upgradeId,
              newLevel: lastResultLevel,
              costPaid: totalCost,
              currency: upgrade.currency,
              firedAt: Date.now(),
            },
          });
          const soundId =
            upgrade.currency === 'spirit_balls'
              ? 'big_upgrade_purchased'
              : 'small_upgrade_purchased';
          playSound(soundId);
          playSound('visible_change_reveal');
          // Single analytics event с total cost и final level.
          emit('upgrade.purchased', {
            upgrade_id: upgradeId,
            new_level: lastResultLevel,
            cost_paid: totalCost,
            currency: upgrade.currency,
          });
          checkArtifactsAfter({
            state: workingState,
            firedActivationIds: cur.firedActivationIds,
          });
          markFunnel('first_upgrade_purchased');
          return purchasedCount;
        },

        dismissUpgradeShowcase() {
          set({ pendingUpgradeShowcase: null });
        },

        fadeStreak() {
          // JB 2026-05-26: «когда идет тип про ритм то надо запаузить чтобы
          // не исчез на глазах пользователя бэйджик ритма пока он читает».
          // Если активна онбординг-подсказка с якорем 'streak' — НЕ гасим
          // стрик. После закрытия подсказки следующий тап ре-армит таймер
          // и нормальный fade продолжится.
          const tip = useOnboardingStore.getState().current;
          if (tip !== null && tip.anchor === 'streak') {
            return;
          }
          const cur = get();
          const finalLength = cur.state.stats.tapsInRhythmCurrent;
          if (finalLength === 0) {
            // Уже сброшен — никаких set, чтобы не триггерить ре-рендер.
            return;
          }
          // UX flow analytics: эмитим streak.broken с финальной длиной.
          // Только если стрик был «активным» (≥2) — single tap не события.
          if (finalLength >= 2) {
            emit('tap.streak.broken', { final_length: finalLength, epoch: cur.state.epoch });
          }
          set({
            state: {
              ...cur.state,
              stats: {
                ...cur.state.stats,
                tapsInRhythmCurrent: 0,
              },
            },
          });
        },

        grantAtlasReward(awarenesses, spirit_balls) {
          if (awarenesses === 0 && spirit_balls === 0) return;
          const cur = get();
          const nextState: GameState = {
            ...cur.state,
            resources: {
              ...cur.state.resources,
              awarenesses: cur.state.resources.awarenesses + awarenesses,
              spirit_balls: cur.state.resources.spirit_balls + spirit_balls,
            },
          };
          set({
            state: nextState,
            multiplier: composeMultiplierForState(nextState),
          });
        },
      };
    },
    {
      name: PERSIST_KEY,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      // Persistим только authoritative game data — runtime UI state
      // (animations, overlays, multiplier cache) восстановим на rehydrate.
      partialize: (s): PersistedGameStore => ({
        state: serializeGameState(s.state),
        firedActivationIds: Array.from(s.firedActivationIds),
        hasSeenCrystallize: s.hasSeenCrystallize,
      }),
      // Merge применяет persisted snapshot к свежему store. UI-поля
      // остаются дефолтные (пустые liveFloatingPhrases, no activation
      // overlay, etc.). Multiplier пересчитывается из restored state.
      merge: (persisted, current) => {
        if (persisted === null || persisted === undefined) return current;
        const p = persisted as PersistedGameStore;
        const restoredState = deserializeGameState(p.state);
        return {
          ...current,
          state: restoredState,
          firedActivationIds: new Set(p.firedActivationIds),
          hasSeenCrystallize: p.hasSeenCrystallize,
          multiplier: composeMultiplierForState(restoredState),
        };
      },
    },
  ),
);

// Dev-only debug handle: позволяет инспектировать/мутировать state из
// DevTools console и preview eval — не попадает в production bundle.
if (import.meta.env.DEV) {
  (window as unknown as { __saiGame: typeof useGameStore }).__saiGame = useGameStore;
}
